/**
 * The Assistant chip's label for the conversation's context (task 772).
 *
 * The chip names **what this chat is about**, not where the browser happens to
 * be — so on a resource page it reads `AI Service: Order Triage`, and anywhere
 * else it is just the page's label.
 *
 * Resolving the name costs nothing in the common case. Every resource detail
 * hook in the app caches its row under `[<resourceType>, id]`, and the
 * assistant's `resourceType` vocabulary (task 746) is exactly those same
 * singular slugs — so asking React Query for that key is a **cache hit against
 * the page the User is already looking at**. The `queryFn` below is only
 * reached when there is no such page (a chat resumed from history, a resource
 * opened in a fresh tab), and even then it dedupes with the page's own fetch
 * rather than racing it.
 */
import { useQuery } from '@tanstack/react-query';
import {
  agentsApi,
  collaborationConfigsApi,
  endpointsApi,
  modelConfigsApi,
  mcpConnectionsApi,
  messageRoutesApi,
  promptsApi,
} from '@/services/api';

/**
 * Product labels for the resource types a conversation can belong to. These are
 * the words the product uses, not the service's slugs — a User has never seen
 * the string `collaboration-config`.
 */
export const RESOURCE_TYPE_LABELS: Record<string, string> = {
  endpoint: 'AI Service',
  'collaboration-config': 'Config',
  agent: 'Agent',
  prompt: 'Prompt',
  'message-route': 'Message Route',
  'model-config': 'Model Config',
  'mcp-connection': 'MCP Connection',
};

/** One reader per type, keyed by the same slug the chip receives. */
const FETCHERS: Record<string, (id: string) => Promise<unknown>> = {
  endpoint: (id) => endpointsApi.getById(id).then((res) => res.data.data),
  'collaboration-config': (id) => collaborationConfigsApi.getById(id).then((res) => res.data.data),
  agent: (id) => agentsApi.getById(id).then((res) => res.data.data),
  prompt: (id) => promptsApi.getById(id).then((res) => res.data.data),
  'message-route': (id) => messageRoutesApi.getById(id).then((res) => res.data.data),
  'model-config': (id) => modelConfigsApi.getById(id).then((res) => res.data.data),
  'mcp-connection': (id) => mcpConnectionsApi.getById(id).then((res) => res.data.data),
};

/** First of the given values that is a non-empty string, trimmed. */
const firstNonEmpty = (...values: unknown[]): string | null => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') return value.trim();
  }
  return null;
};

/**
 * The display name a resource row carries.
 *
 * `name` for almost everything; an AI Service follows the shared naming rule in
 * `utils/endpointLabel.ts` — `name → description → path` — so the chip reads the
 * same string the service's own page and every list row show. Read generically
 * rather than through a per-type field map so a resource whose shape differs
 * slightly degrades to the type label instead of throwing.
 */
const displayName = (row: unknown, resourceType?: string): string | null => {
  if (!row || typeof row !== 'object') return null;
  const record = row as Record<string, unknown>;
  if (resourceType === 'endpoint') {
    return firstNonEmpty(record.name, record.description, record.path);
  }
  const candidate = record.name ?? record.path;
  return typeof candidate === 'string' && candidate.trim() !== '' ? candidate.trim() : null;
};

/**
 * The chip's text for a resource context, and the full name behind it.
 *
 * Returns the type label alone while the name is loading or unavailable — the
 * chip is never blank and never shows a raw id, because a chip that flickers
 * between states is worse than one that fills in.
 */
export function useResourceContextLabel(
  resourceType: string | undefined,
  resourceId: string | undefined
): { label: string | null; fullName: string | null } {
  const fetcher = resourceType ? FETCHERS[resourceType] : undefined;

  const { data } = useQuery({
    // Deliberately the SAME key the resource's own detail hook uses.
    queryKey: [resourceType, resourceId],
    queryFn: () => fetcher!(resourceId!),
    enabled: Boolean(resourceType && resourceId && fetcher),
    // A name is not worth a retry storm; the chip falls back to the type label.
    retry: false,
    staleTime: 60_000,
  });

  if (!resourceType) return { label: null, fullName: null };

  const typeLabel = RESOURCE_TYPE_LABELS[resourceType] ?? resourceType;
  const name = displayName(data, resourceType);

  return {
    label: name ? `${typeLabel}: ${name}` : typeLabel,
    fullName: name,
  };
}

export default useResourceContextLabel;
